import { useState } from "react";
import { CashfreeVendorSettlementComponent, LedgerModel, LedgerType } from "../utils/Models";
import { Colors } from "../../config/Constants";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";

// Color maps
const ledgerTypeToText: { [k in LedgerType]: string } = {
  [LedgerType.MANUAL_CREDIT]: 'Adjustment: CREDIT',
  [LedgerType.MANUAL_DEBIT]: 'Adjustment: DEBIT',
  [LedgerType.ORDER_SETTLEMENT]: 'Order Settlement',
}
const ledgerTypeToColorMap: { [k in LedgerType]: string } = {
  [LedgerType.MANUAL_CREDIT]: Colors.primary,
  [LedgerType.MANUAL_DEBIT]: Colors.failureRed,
  [LedgerType.ORDER_SETTLEMENT]: Colors.primary,
}

// Ledger card function
export function LedgerCard(props: { data: LedgerModel }) {

  // Local state
  const [state, setState] = useState<{
    expanded: boolean,
    loading?: boolean;
    data?: CashfreeVendorSettlementComponent[],
    expandedMessage?: string
  }>({ expanded: false });

  // Global state
  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Calculate column widths
  const columnWidths = {
    ORDER_ID: isPhone ? '75px' : '120px',
    TYPE: isPhone ? '65px' : '120px',
    AMOUNT: isPhone ? '68px' : '100px',
    BALANCE: isPhone ? '68px' : '100px'
  }

  // Return DOM
  return (
    <Box className='gbk-dg-row-phone'>
      <Box className='gbk-dg-row-phone-header' key={1} >
        <span style={{ color: ledgerTypeToColorMap[props.data.adjustmentType] }}>
          {ledgerTypeToText[props.data.adjustmentType]}
        </span>
      </Box>
      <Box
        className='gbk-dg-row-phone-body'
        key={2}
      >
        {/* Left details */}
        <div style={{ display: 'inline-block', verticalAlign: 'top' }}>
          Amount: <b style={{ color: Colors.primary }}>
            ₹
            {props.data.adjustment ? props.data.adjustment.value1 : ''}
            {props.data.settlement ? props.data.settlement.settlementAmount : ''}
          </b> <br />
          Initiated On: <b style={{ fontSize: '14px' }}>
            {props.data.adjustment ? DateTime.fromMillis(Number(props.data.adjustment.startTime)).toLocaleString(DateTime.DATETIME_MED) : ''}
            {props.data.settlement?.initiatedOn ? DateTime.fromISO(props.data.settlement.initiatedOn).toLocaleString(DateTime.DATETIME_MED) : ''}
          </b> <br />
          Updated On: <b style={{ fontSize: '14px' }}>
            {props.data.adjustment ? DateTime.fromMillis(Number(props.data.adjustment.endTime)).toLocaleString(DateTime.DATETIME_MED) : ''}
            {props.data.settlement?.updatedOn ? DateTime.fromISO(props.data.settlement.updatedOn).toLocaleString(DateTime.DATETIME_MED) : ''}
          </b> <br />
          UTR: <b style={{ fontSize: '14px' }}>
            {props.data.adjustment ? DateTime.fromMillis(Number(props.data.adjustment.endTime)).toLocaleString(DateTime.DATETIME_MED) : ''}
            {props.data.settlement ? props.data.settlement.utr : ''}
          </b> <br />
          Status: <b style={{ fontSize: '14px' }}>
            {props.data.adjustment ? props.data.adjustment.status : ''}
            {props.data.settlement ? props.data.settlement.status : ''}
          </b>
        </div>

        {/* Load details button */}
        <div style={{ position: 'absolute', right: '20px', bottom: 'calc(50% - 20px)', display: props.data.settlement?.status === 'SUCCESS' ? 'block' : 'none' }}>
          <Button
            variant="outlined"
            // size="large"
            style={{ width: '40px' }}
            onClick={() => {
              const newExpandedState = !state.expanded;
              if (newExpandedState && !state.data) {
                setState({ ...state, loading: true });
                (async () => {
                  try {
                    const { data } = await GobikesApi.get(`/vendors/ledger/settlements/${props.data.settlement?.settlementId}`, { headers: { 'vendor-token': authCookie } });
                    let balance = 0;
                    let dataList = data.data;
                    dataList.sort((a: CashfreeVendorSettlementComponent, b: CashfreeVendorSettlementComponent) => {
                      const x = a.merchant_order_id;
                      const y = b.merchant_order_id;
                      return ((x < y) ? -1 : ((x > y) ? 1 : 0));
                    })
                    dataList = dataList.map((d: any, i: number) => { d.id = i; return d; })
                      .filter((d: any) => {
                        if (d.type === 'vendor_settlement') return false;
                        const credit = !isNaN(parseFloat(d.credit)) ? Number(d.credit) : '';
                        const debit = !isNaN(parseFloat(d.debit)) ? Number(d.debit) : '';
                        balance += credit ? Number(credit) : Number(debit) * -1;
                        d.balance = balance.toFixed(2);
                        return true;
                      });
                    setState({ ...state, expanded: newExpandedState, data: dataList });
                  } catch (e: any) {
                    const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                    setState({ ...state, expandedMessage: errorMessage, expanded: newExpandedState });
                  }
                })();
              } else {
                setState({ ...state, expanded: newExpandedState });
              }
            }}
          >
            {state.expanded ? (
              <span>
                <span className="gbk-icon-span fs-large"> <KeyboardArrowUp />  </span>
              </span>
            ) : (
              <span>
                <span className="gbk-icon-span fs-large"> <KeyboardArrowDown />  </span>
              </span>
            )}
          </Button>
        </div>
      </Box>

      {/* Expanded details */}
      <Box
        className="gbk-dg-row-phone-footer"
        sx={{ display: state.expanded ? 'block' : 'none', padding: '0 10px' }}
      >
        {state.expandedMessage ? state.expandedMessage : (
          <ul className="gbk-joined-list" style={{ backgroundColor: '#fff' }}>
            <li className="header" >
              <div className="float-left" style={{ width: columnWidths.ORDER_ID }}>Order ID</div>
              <div className="float-left" style={{ width: columnWidths.TYPE, textAlign: 'center' }}>Type</div>
              <div className="float-right" style={{ width: columnWidths.BALANCE, paddingLeft: '6px' }}>Balance</div>
              <div className="float-right" style={{ width: columnWidths.AMOUNT }}>Amount</div>
            </li>
            {
              (state.data || []).map((d: CashfreeVendorSettlementComponent, i: number) => {
                let transactionType = d.type === 'MANUAL_VENDOR_ADJUSTMENT' ? 'Adjustment' : d.type;
                let orderId: string | number = d.merchant_order_id;
                if (isNaN(parseFloat(d.merchant_order_id))) {
                  if (d.orderExtDetails?.orderId) {
                    orderId = d.orderExtDetails.orderId;
                    transactionType = 'Extension';
                  } else if (d.orderCustomPaymentDetails?.orderId) {
                    orderId = d.orderCustomPaymentDetails.orderId;
                    transactionType = 'Custom Payment';
                  } else {
                    orderId = '-';
                  }
                }
                const credit = !isNaN(parseFloat(d.credit)) ? Number(d.credit).toFixed(2) : '';
                const debit = !isNaN(parseFloat(d.debit)) ? Number(d.debit).toFixed(2) : '';
                const sign = debit ? '-' : '';
                const amount = debit ? debit : credit;
                return (
                  <li key={i}>
                    <div className="float-left" style={{ width: columnWidths.ORDER_ID }}>
                      {orderId}
                    </div>
                    <div className="float-left" style={{ width: columnWidths.TYPE, textTransform: 'capitalize', textAlign: 'center' }}>
                      {transactionType}
                    </div>
                    <div className="float-right roboto" style={{ width: columnWidths.BALANCE, paddingLeft: '10px' }}>
                      ₹ {(d as any).balance}
                    </div>
                    <div
                      className="float-right roboto"
                      style={{ width: columnWidths.AMOUNT, textAlign: 'left', color: credit ? Colors.successGreen : Colors.failureRed }}
                    >
                      {sign ? sign : <span>&nbsp;</span>} ₹ {amount}
                    </div>
                  </li>
                )
              }
              )
            }
          </ul>
        )}
      </Box>
    </Box>
  )
}